import axios from 'axios';

// Since everything is served from the same origin (Fastify on port 8181),
// we use relative URLs without any baseURL transformation
// This ensures requests go to /api/... on the current domain
const api = axios.create({
  // DO NOT set baseURL - use absolute paths instead
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to log actual URLs
api.interceptors.request.use((config) => {
  console.debug('API Request:', {
    url: config.url,
    method: config.method,
    fullURL: window.location.origin + config.url,
  });
  return config;
});

// Add error interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.debug('API Success:', {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
    });
    return response;
  },
  (error) => {
    console.error('API Error Details:', {
      message: error.message,
      code: error.code,
      url: error.config?.url || 'NO_URL',
      fullURL: error.config ? window.location.origin + (error.config.url || '') : 'UNKNOWN',
      hasResponse: !!error.response,
      hasRequest: !!error.request,
    });

    if (error.response) {
      // Server responded with an error status
      console.error('API Error Response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      });
    } else if (error.request) {
      // Request was made but no response received
      console.error('API Error: No response received', {
        readyState: error.request.readyState,
        status: error.request.status,
        statusText: error.request.statusText,
        message: error.message,
      });
    } else {
      // Error during request setup
      console.error('API Error (Setup):', error.message);
    }
    return Promise.reject(error);
  }
);

export interface Product {
  itemId: string;
  title: string;
  price: number;
  currency: string;
  condition: string;
  categoryId: string;
  categoryName?: string;
  imageUrls?: string[];
  itemUrl: string;
  seller: {
    username: string;
    feedbackScore?: number;
    feedbackPercentage?: number;
  };
  location?: {
    country: string;
    postalCode?: string;
  };
  shipping?: {
    cost: number;
    type: string;
  };
  listingType: string;
  startTime: string;
  endTime?: string;
  quantity?: number;
  quantitySold?: number;
  watchCount?: number;
  description?: string;
  feedType: string;
  importedAt: string;
  updatedAt: string;
}

export interface SearchResult {
  items: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface FeedImportStatus {
  id: string;
  feedType: string;
  category: string;
  status: 'pending' | 'downloading' | 'processing' | 'completed' | 'failed';
  startTime: string;
  endTime?: string;
  totalItems?: number;
  processedItems: number;
  failedItems: number;
  downloadUrl?: string;
  filePath?: string;
  fileSize?: number;
  error?: string;
  progress: number;
}

export interface FeedStats {
  totalProducts: number;
  byFeedType: {
    BOOTSTRAP: number;
    DAILY: number;
    HOURLY: number;
  };
  lastImport?: {
    feedType: string;
    timestamp: string;
    itemsProcessed: number;
  };
  activeImports: number;
  failedImports: number;
}

export const searchProducts = async (params: any): Promise<SearchResult> => {
  const response = await api.get('/api/products/search', { params });
  return response.data;
};

export const getProductById = async (id: string): Promise<Product> => {
  const response = await api.get(`/api/products/${id}`);
  return response.data;
};

export const getFeedStats = async (): Promise<FeedStats> => {
  const response = await api.get('/api/feeds/stats');
  return response.data;
};

export const getImportStatus = async (
  id?: string
): Promise<FeedImportStatus | FeedImportStatus[]> => {
  const url = id ? `/api/feeds/status/${id}` : '/api/feeds/status';
  const response = await api.get(url);
  return response.data;
};

export const getActiveImports = async (): Promise<FeedImportStatus[]> => {
  const response = await api.get('/api/feeds/active');
  return response.data;
};

export const getPendingImports = async (): Promise<FeedImportStatus[]> => {
  const response = await api.get('/api/feeds/pending');
  return response.data;
};

export const getCompletedImports = async (): Promise<FeedImportStatus[]> => {
  const response = await api.get('/api/feeds/completed');
  return response.data;
};

export const startImport = async (
  feedType: string,
  category?: string | string[]
): Promise<any> => {
  const payload: any = { feedType };

  if (Array.isArray(category)) {
    payload.categories = category;
  } else if (category) {
    payload.categories = [category];
  } else {
    payload.categories = ['ALL_ACTIVE'];
  }

  const response = await api.post('/api/feeds/import', payload);
  return response.data;
};

export interface Category {
  categoryId: string;
  categoryName: string;
  level: number;
  isLeaf: boolean;
  parentId?: string;
}

export const getCategories = async (onlyLeaf: boolean = false): Promise<Category[]> => {
  const response = await api.get('/api/categories', {
    params: { onlyLeaf },
  });
  return response.data.categories || [];
};

export const getTopLevelCategories = async (): Promise<Category[]> => {
  const response = await api.get('/api/categories/top-level');
  return response.data.categories || [];
};

export const getChildCategories = async (parentId: string): Promise<Category[]> => {
  const response = await api.get(`/api/categories/${parentId}/children`);
  return response.data.categories || [];
};

export default api;
